import { useMsal } from '@azure/msal-react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { apiScopes } from './msalConfig';

export interface CurrentUser {
  upn: string;
  display_name: string | null;
  is_admin: boolean;
  is_bootstrap_admin: boolean;
  allowed_reports: string[];
  can_edit_prices: boolean;
  // Wystawianie ZO w Enovie z raportu Oprogramowanie: uprawnienie `opr.zo.issue`
  // ORAZ funkcja skonfigurowana po stronie backendu (token Enovy). Front nie zna
  // konfiguracji serwera — przycisk pokazuje wyłącznie na podstawie tej flagi.
  can_issue_opr_zo?: boolean;
}

// Niesie status HTTP + komunikat z backendu (np. „konto oczekuje na akceptację”),
// żeby bramka dostępu mogła pokazać sensowny tekst zamiast generycznego błędu.
export class MeError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = 'MeError';
  }
}

export async function fetchMe(accessToken: string): Promise<CurrentUser> {
  const response = await fetch('/api/me', {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!response.ok) {
    let detail = `Błąd ${response.status}`;
    try {
      const body = (await response.json()) as { detail?: string };
      if (body?.detail) detail = body.detail;
    } catch {
      /* odpowiedź bez JSON — zostaje status */
    }
    throw new MeError(response.status, detail);
  }
  return (await response.json()) as CurrentUser;
}

export function useMe(): UseQueryResult<CurrentUser> {
  const { instance, accounts } = useMsal();
  const account = accounts[0];

  return useQuery({
    queryKey: ['me'],
    enabled: Boolean(account),
    // 401/403 są terminalne (brak dostępu / pending) — nie ponawiamy.
    retry: (count, error) =>
      !(error instanceof MeError && (error.status === 401 || error.status === 403)) && count < 2,
    queryFn: async () => {
      const result = await instance.acquireTokenSilent({ scopes: apiScopes, account });
      return fetchMe(result.accessToken);
    },
  });
}
